/// <reference types="@netfront/ekardo-content-library/dist/types/window" />
import '@netfront/ui-library/dist/esm/css/index.css';
import '@netfront/ui-library/dist/css/global.css';
import '@netfront/ui-library/dist/css/helpers.css';
import '@netfront/ekardo-content-library/dist/esm/css/index.css';

import 'react-circular-progressbar/dist/styles.css';

import '../styles/components/index.css';
import '../styles/content-library/index.css';
import '../styles/globals.css';
import '../styles/ui-library/index.css';
import '../styles/utils.css';
import '../styles/cms-custom.css';
import 'animate.css';

import { AppProps } from 'next/app';
import isNil from 'lodash.isnil';
import omitBy from 'lodash.omitby';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { ApolloClientsProvider } from '@netfront/apollo-providers';
import { ContentPageProvider, useGoogleAnalytics, Toaster } from '@netfront/ekardo-content-library';

import { useUser } from '@netfront/gelada-identity-library';
import Head from 'next/head';
import LogRocket from 'logrocket';

function AppInner({ Component, pageProps, googleAnalyticsMeasurementId, isGoogleAnalyticsDebugMode }: Pick<AppProps, 'Component' | 'pageProps'> & { googleAnalyticsMeasurementId: string; isGoogleAnalyticsDebugMode: boolean }) {
  const { trackPageView, canRunAuthenticated } = useGoogleAnalytics();
  const { events } = useRouter();
  const { getUser } = useUser();

  const user = getUser();

  const [gtagConfigParams, setGtagConfigParams] = useState<ReturnType<typeof omitBy>>();

  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    LogRocket.init('sfzpuh/parenting-and-teens');
    if (user) {
      LogRocket.identify(String(user.id), {
        name: `${user.firstName ?? ''} ${user.lastName ?? ''}`,
        email: user.credential?.email ?? '',
      });
    }
  }

  useEffect(() => {
    if (Boolean(googleAnalyticsMeasurementId) && (canRunAuthenticated || !user)) {
      trackPageView(window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRunAuthenticated]);

  useEffect(() => {
    const handleRouteChange = () => {
      if (Boolean(googleAnalyticsMeasurementId) && (canRunAuthenticated || !user)) {
        trackPageView(window.location.pathname);
      }
    };

    events.on('routeChangeComplete', handleRouteChange);

    return () => events.off('routeChangeComplete', handleRouteChange);
  }, []);

  useEffect(() => {
    if (gtagConfigParams) {
      return;
    }

    setGtagConfigParams(() =>
      omitBy(
        {
          debug_mode: isGoogleAnalyticsDebugMode ? true : undefined,
          send_page_view: false,
          user_id: String(user?.id),
          userId: String(user?.id),
          app_user_id: String(user?.id),
        },
        isNil,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gtagConfigParams, user]);

    useEffect(() => {
    if (gtagConfigParams && typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', googleAnalyticsMeasurementId, gtagConfigParams);
    }
  }, [gtagConfigParams, googleAnalyticsMeasurementId]);


  return (
    <>
      <Head>
        {/* Global Site Tag (gtag.js) - Google Analytics */}
        <script src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsMeasurementId}`} async />
        <script
          dangerouslySetInnerHTML={{
            __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          `,
          }}
        />
      </Head>
      <ContentPageProvider>
        <Component {...pageProps} />
      </ContentPageProvider>
    </>
  );
}

function App({ Component, pageProps }: AppProps) {
  const googleAnalyticsMeasurementId = String(process.env.REACT_APP_GOOGLE_ANALYTICS);
  const isGoogleAnalyticsDebugMode = String(process.env.REACT_APP_GOOGLE_ANALYTICS_DEBUG_MODE).toUpperCase() === 'Y';

  return (
    <div id="root">
      <Toaster
        toastOptions={{
          duration: 2500,
        }}
      />
      <ApolloClientsProvider options={{ endpointSource: { source: 'environment variables' } }}>
        <AppInner
          Component={Component}
          pageProps={pageProps}
          googleAnalyticsMeasurementId={googleAnalyticsMeasurementId}
          isGoogleAnalyticsDebugMode={isGoogleAnalyticsDebugMode}
        />
      </ApolloClientsProvider>
    </div>
  );
}

// eslint-disable-next-line import/no-default-export
export default App;